<template>
  <div>
    <div :class="rowClass">
      <div v-if="showProjectId" class="cell">{{ formatProjectId(work.internal_project_id) }}</div>
      <div class="cell">{{ work.project_name }}</div>
      <div v-if="showTimestamp" class="cell">
        {{ getTimestampCellData() }}
      </div>
      <div class="cell">
        {{ getHoursCellData() }}
      </div>
      <div class="cell">
        {{ getCellData(report['employee_report']) }}
      </div>
      <div class="cell cell_grid" :class="getCellClass(report['employee_report'], report['approved_report'])">
        <BaseCheckbox
            v-model="paid.value"
            class="mr-2"
            @input="setPaidChanges(!paid.value)"
            :disabled="!checkEditAccess || report['paid_report'] > 0"
            :key="Number(paid.value)"
        />
        {{ getCellData(report['approved_report']) }}
        <div class="d-flex">
          <v-btn @click.stop="showNote = true, approvedNote = true, dialog = true" v-if="report['approved_report'] > 0 && report['approved_notes']" icon small
                 class="ml-n1 my-n1">
            <v-icon color="#277FAF">mdi-information</v-icon>
          </v-btn>
          <template v-if="report['client_bill_report_sign_photo']">
            <v-dialog v-if="fileType === 'image'" v-model="modal" width="1200">
              <template v-slot:activator="{ on, attrs }">
                <v-btn text icon v-bind="attrs" v-on="on" class="ma-n2">
                  <slot name="icon">
                    <IconTimeControlBill icon="green"/>
                  </slot>
                </v-btn>
              </template>
              <v-img :src="apiDomain + '/' + report['client_bill_report_sign_photo']" class="mx-auto" contain>
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="secondary lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-btn @click="modal = false" fixed top right fab small dark class="mt-16">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-btn :href="apiDomain + '/' + report['client_bill_report_sign_photo']" target="_blank" fixed bottom right fab dark class="mb-16" color="#277faf">
                <IconsButton icon="button-save" width="30" height="30" color="white"/>
              </v-btn>
            </v-dialog>
            <PdfModalShow v-else :url="report['client_bill_report_sign_photo']" btn-class="ma-n2"/>
          </template>
        </div>
      </div>
      <v-text-field
          v-if="edit.paid"
          v-model="edit.data"
          dense hide-details outlined
          :autofocus="!dialog"
          class="f14 none_border"
          :type="work.is_hourly === 1 || isExtra ? 'time' : 'numeric'"
          :rules="[v => !!v, v => v !== '0', v => v !== '00:00']"
          validate-on-blur
          @change="changePaid(edit.data)"
          @blur="changePaid(edit.data)"
      />
      <v-hover v-else v-slot="{ hover }">
        <div
            class="cell d-flex justify-space-between"
            @click="checkEditAccess && onEdit(work.is_hourly === 1 || isExtra)"
            :class="getCellClass(report['approved_report'], toPaid)  + (checkEditAccess ? ' pointer' : '')"
        >
          <div>
            <template v-if="toPaid > 0">
              {{ getCellData(toPaid) }}
            </template>
          </div>
          <div>
            <v-btn @click.stop="showNote = true, dialog = true" v-if="toPaid > 0 && report['paid_notes']" icon small
                   class="ml-n1 my-n1">
              <v-icon color="#277FAF">mdi-information</v-icon>
            </v-btn>
            <v-icon size="18" class="ml-auto" :color="hover ? 'primary' : ''">mdi-pencil</v-icon>
          </div>
        </div>
      </v-hover>
      <div class="cell">{{ work.salary ? $formatCurrency(work.salary) + ' €/' + (work?.measure || localStorage.translations.pages.common.measure.hours_min) : '—' }}</div>
      <div class="cell">{{ report['salary_sum'] ? $formatCurrency(report['salary_sum']) + ' €' : '—' }} </div>
      <template v-if="responsibleShow && checkResponsible">
        <div class="cell responsibleCell">
          {{ report.project_responsible_salary ? $formatCurrency(report.project_responsible_salary) + ' €/' + (work?.measure || localStorage.translations.pages.common.measure.hours_min) : '—' }}
        </div>
        <div class="cell responsibleCell">
          <BaseCheckbox
            v-if="report.responsible_user_id !== null && (!checkEditAccess || report['paid_report'] > 0)"
            v-model="paid_responsible.value"
            class="mr-2"
            @input="setPaidResponsibleChanges(!paid_responsible.value)"
            :disabled="!checkEditAccess || report['project_responsible_approved'] > 0"
            :key="Number(paid_responsible.value)"
          />
          {{ report.project_responsible_salary_sum ? $formatCurrency(report.project_responsible_salary_sum) + ' €' : '—' }}
        </div>
        <div class="cell responsibleCell">
          {{ report.responsible_user_id ? report.responsible_user_first_name + ' ' + report.responsible_user_last_name : '—' }}
        </div>
      </template>
      
      <div class="grid_days_inner_note">
        <div class="cell">
          {{ work.name }}
        </div>
        <v-textarea
            v-if="salaryNote"
            :rows="1"
            v-model="report['salary_notes']"
            auto-grow outlined hide-details autofocus
            class="f14 none_border"
            @change="changeSalaryNote()"
            @blur="changeSalaryNote()"
        />
        <div v-else class="cell">
          <v-hover v-if="checkEditAccess" v-slot="{ hover }">
            <div @click="salaryNote = true" class="d-flex flex-grow-1 align-center justify-space-between pointer">
              <v-sheet color="transparent" width="100%" class="text-pre-line">
                {{ report['salary_notes'] }}
              </v-sheet>
              <v-btn icon x-small text :color="hover ? 'primary' : ''" @click.stop="salaryNote = true">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-hover>
        </div>
      </div>
    </div>
    <v-dialog width="600" v-if="dialog" v-model="dialog">
      <v-card class="pt-4 px-4 pb-4">
        <v-btn icon absolute right top class="mt-n2 mr-n2" @click="resetEdit">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="d-flex justify-center pb-4">
          <v-icon large color="#277FAF">mdi-information</v-icon>
        </div>
        <template v-if="showNote">
          <v-textarea
            v-if="approvedNote"
              v-model="report['approved_notes']"
              auto-grow outlined hide-details readonly
              rows="4"
          />
          <v-textarea
            v-else
            v-model="report['paid_notes']"
            auto-grow outlined hide-details readonly
            rows="4"
          />
        </template>
        <v-textarea v-else v-model="edit.note" auto-grow outlined hide-details autofocus rows="4"/>
        <template v-if="showNote">
          <div class="text-end f12 my-2">
            <template v-if="approvedNote">
              <div v-if="report['approved_by_user']">
                {{ report['approved_by_user'].first_name + ' ' + report['approved_by_user'].last_name }}
              </div>
              <div v-if="report['approved_datetime']">
                {{ report['approved_datetime'] }}
              </div>
            </template>
            <template v-else>
              <div v-if="report['pain_by_user']">
                {{ report['pain_by_user'].first_name + ' ' + report['pain_by_user'].last_name }}
              </div>
              <div v-if="report['paid_datetime']">
                {{ report['paid_datetime'] }}
              </div>
            </template>
          </div>
          <div class="d-flex justify-center">
            <v-hover v-slot="{ hover }">
              <v-btn
                  fab :outlined="!hover"
                  color="#277FAF"
                  dark
                  @click="resetEdit"
              >
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </v-hover>
          </div>
        </template>
        <div v-else class="d-flex justify-space-around mt-4">
          <v-hover v-slot="{ hover }">
            <v-btn fab :outlined="!hover" :dark="hover" color="red" @click="resetEdit">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-hover>
          <v-hover v-slot="{ hover }">
            <v-btn
                fab depressed
                :outlined="!hover"
                :dark="hover || !!edit.note"
                color="#26bc2c"
                :disabled="edit.data === 0 ? false : !edit.note"
                @click="setApproveChanges(edit.data === 0)"
            >
              <v-icon>mdi-check</v-icon>
            </v-btn>
          </v-hover>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BaseCheckbox from '../../../base/BaseCheckbox'
import PdfModalShow from 'components/pdf/PdfModalShow'
import IconTimeControlBill from "components/icons/timeControl/IconTimeControlBill";
import {mapGetters} from "vuex";
import IconsButton from "components/icons/button/IconsButton";

export default {
  name: 'FinanceSalarySliceShowTableByDaysItem',
  components: {IconsButton, IconTimeControlBill, PdfModalShow, BaseCheckbox},
  props: {
    isExtra: Boolean,
    showProjectId: {
      type: Boolean,
      default: false,
    },
    showTimestamp: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: () => {
        return {}
      },
    },
    work: {
      type: Object,
      default: () => {
        return {}
      },
    },
    report: {
      type: Object,
      default: () => {
        return {}
      },
    },
    onUpdate: {},
    responsibleShow: Boolean,
    checkResponsible: Boolean,
  },
  data: () => ({
    edit: {
      paid: false,
      data: 0,
      note: '',
    },
    modal: false,
    dialog: false,
    notePaid: false,
    noteOnly: false,
    showNote: false,
    salaryNote: false,
    toPaid: '0',
    toPaidNotes: '',
    approvedNote: false,
    responsible_approved: false,
    toPaidResponsible: '0'
  }),
  computed: {
    ...mapGetters(['apiDomain']),
    rowClass() {
      let baseClass = ''
      if (this.responsibleShow && this.checkResponsible) {
        if (this.showTimestamp) {
          baseClass = this.showProjectId ? 'grid_days_inner_with_timestamp_project_id' : 'grid_days_inner_with_timestamp'
        } else {
          baseClass = this.showProjectId ? 'grid_days_inner_project_id' : 'grid_days_inner'
        }
      } else {
        if (this.showTimestamp) {
          baseClass = this.showProjectId
            ? 'grid_days_responsible_inner_with_timestamp_project_id'
            : 'grid_days_responsible_inner_with_timestamp'
        } else {
          baseClass = this.showProjectId ? 'grid_days_responsible_inner_project_id' : 'grid_days_responsible_inner'
        }
      }
      return baseClass + (this.isExtra ? '' : ' fw500')
    },
    paid() {
      let temp = 0
      if (typeof this.toPaid === 'string') {
        temp = this.toPaid.split(':')
        temp = Number(temp[0]) * 60 + Number(temp[1])
      }
      return {value: this.toPaid > 0 || temp > 0}
    },
    paid_responsible() {
      let temp = 0
      if (typeof this.toPaidResponsible === 'string') {
        temp = this.toPaidResponsible.split(':')
        temp = Number(temp[0]) * 60 + Number(temp[1])
      }
      return {value: this.toPaidResponsible > 0 || temp > 0}
    },
    checkEditAccess() {
      return this.$userCheckAccess({
        abilities: 'edit_salary',
      })
    },
    fileType() {
      if (this.report['client_bill_report_sign_photo']) {
        if (typeof this.report['client_bill_report_sign_photo'] === 'string')
          return this.report['client_bill_report_sign_photo'].split('.')[1] === 'pdf' ? 'application' : 'image'
        return this.report['client_bill_report_sign_photo'].type.split('/')[0]
      }
      return ''
    },
    changedData() {
      return this.toPaid !== this.report['paid_report']
    },
    changedDataResponsible() {
      return this.toPaidResponsible > 0 && Number(this.report['project_responsible_approved']) === 0
    },
  },
  mounted() {
    this.toPaid = this.report['paid_report']
    this.toPaidNotes = this.report['paid_notes'] || ''
    this.toPaidResponsible = this.report['project_responsible_approved']
  },
  methods: {
    formatProjectId(value) {
      return value === 0 || value ? value : '—'
    },
    formatValue(value) {
      let temp = Number(value)
      if (this.work.is_hourly === 1 || this.isExtra) {
        if (typeof value === 'string') {
          temp = value.split(':')
          temp = Number(temp[0]) * 60 + Number(temp[1])
        }
      }
      return temp
    },

    setPaidChanges(onClear) {
      this.toPaid = onClear ? 0 : this.toPaid || this.report['approved_report']
    },
    setPaidResponsibleChanges(onClear) {
      this.toPaidResponsible = onClear ? 0 : this.toPaidResponsible || this.report['project_responsible_salary_sum']
    },
    async setApproveChanges(onClear) {
      let data = {}
      data.id = this.report.id
      let value = onClear
          ? 0
          : this.noteOnly
              ? this.toPaid
              : this.formatValue(this.edit.data) ||
              this.toPaid ||
              this.report['approved_report']
      this.work.is_hourly === 1 || this.isExtra
          ? (data['paid_hours'] = value)
          : (data['paid_size'] = value)
      data['paid_notes'] = this.noteOnly
          ? this.edit.note
          : this.edit.note || this.report['paid_notes']
      /*if (this.onUpdate)
        this.onUpdate(
            Array(data),
            onClear ? false : this.report['approved_report'] > 0,
            this.isExtra,
        )*/
      this.report['paid_notes'] = data['paid_notes']
      this.toPaidNotes = data['paid_notes']
      this.toPaid = value
      this.resetEdit()
    },

    resetEdit() {
      this.edit = {
        approved: false,
        paid: false,
        data: 0,
        note: '',
      }
      this.dialog = false
      this.notePaid = false
      this.noteOnly = false
      this.showNote = false
      this.salaryNote = false
    },

    onEdit(hourly) {
      let value = this.toPaid || this.report['approved_report']
      if (hourly) {
        this.edit.data =
            String(Math.floor(value / 60)).padStart(2, '0') +
            ':' +
            String(value % 60).padStart(2, '0')
      } else this.edit.data = value
      this.edit.note = this.report['paid_notes'] || ''
      this.edit['paid'] = true
    },

    changePaid(value) {
      if (value !== '0' && value !== '00:00') {
        let control = this.report['approved_report']
        let result = this.formatValue(value)
        if (result === control) {
          this.toPaid = result
          this.resetEdit()
        } else {
          this.notePaid = true
          this.dialog = true
        }
      }
    },

    changeValue(value) {
      let control = this.toPaid
      if (this.formatValue(value) === control) this.resetEdit()
      else {
        let data = this.report['approved_report']
        if (this.formatValue(value) > 0) {
          if (this.formatValue(value) !== data) {
            this.notePaid = true
            this.dialog = true
          } else this.setApproveChanges(false)
        } else this.setApproveChanges(true)
      }
    },

    editNote() {
      this.edit.note = this.report['paid_notes']
      this.noteOnly = true
      this.dialog = true
    },

    changeSalaryNote() {
      let data = {}
      data.id = this.report.id
      data['salary_notes'] = this.report['salary_notes']
      if (this.onUpdate)
        this.onUpdate(Array(data), false, this.isExtra)
    },

    formatNumber(num) {
      return Math.round(num * 100) / 100
    },

    getCellData(value) {
      return this.work.is_hourly === 1 || this.isExtra
        ? this.$formatTime(value)
        : this.formatNumber(value) + ' ' + this.work.measure
    },
    getHoursCellData() {
      return this.$formatTime(this.getHoursValue())
    },
    getTimestampCellData() {
      const start = this.extractTime(this.report?.work_started_at)
      const end = this.extractTime(this.report?.work_ended_at)
      if (!start && !end) return '—'
      return `${start || '—'}-${end || '—'}`
    },
    getHoursValue() {
      if (this.work.is_hourly === 0) return Number(this.report?.piecework_time_hour || 0)
      return this.report['employee_report']
    },
    extractTime(value) {
      if (!value) return ''
      const matched = String(value).match(/(\d{2}:\d{2})/)
      return matched ? matched[1] : ''
    },

    getCellClass(value1, value2) {
      return value1 === value2 || value2 === 0
        ? ''
        : value1 > value2
          ? 'red lighten-5'
          : 'green lighten-5'
    }
  },
}
</script>

<style scoped lang="scss">
.cell_grid {
  width: 100%;
  display: grid !important;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto;
}

.none_border {
  :deep() textarea {
    margin: 0 !important;
    min-height: 24px !important;
  }
}
</style>
